/**
 * PATH: packages/ui/src/versionWatcher/dismissalState.ts
 * FILE_ID: UI-VERSION-UTIL-001
 * PURPOSE: Pure transition logic for VersionWatcher's update / dismissal state.
 * WHY:
 *   The previous implementation had a sticky-dismissal bug: once an
 *   operator dismissed a deploy banner ("X" pill), the `dismissed`
 *   flag stayed `true` for the rest of the tab's lifetime. When a
 *   second deploy landed in the same session, the polling loop did
 *   detect it and called setUpdateAvailable() with the new version
 *   — but the banner was gated by `&& !dismissed`, so it stayed
 *   silent. Operators then asked "if you updated it why did it not
 *   autorefresh" (2026-05-08 incident) and we couldn't answer.
 *
 *   Splitting the transition into a pure function lets us:
 *     1. Unit-test every branch of the truth table.
 *     2. Make the "first-detection vs. re-detection vs. brand-new
 *        version" distinction explicit and review-friendly.
 *     3. Guarantee each NEW version resets the dismissal flag
 *        without accidentally re-resetting on every poll.
 *
 * FLOW:
 *   ┌───────────────────────┐    ┌─────────────────────┐
 *   │ prev state +          │ →  │ next state          │
 *   │ deployed version from │    │ (updateAvailable +  │
 *   │ /version.json poll    │    │  dismissed +        │
 *   │                       │    │  shouldAnnounce)    │
 *   └───────────────────────┘    └─────────────────────┘
 */

export interface UpdateInfo {
  /** The deployed version we're prompting the user to upgrade TO. */
  nextVersion: string
  /** ISO timestamp from /version.json's `builtAt`. Null when the
   *  manifest didn't include one (legacy bundles before the stamp
   *  plugin landed). */
  nextBuiltAt: string | null
}

export interface DismissalState {
  /** Currently-shown update prompt, if any. Null when the loaded
   *  bundle matches production. */
  updateAvailable: UpdateInfo | null
  /** True when the operator clicked the X on the banner. Sticky for
   *  the CURRENT detected version only — when a brand-new version
   *  is detected we reset it so the next deploy's banner can show. */
  dismissed: boolean
  /** The last version we fired the cross-app `quotech.versionAvailable`
   *  event for. Used to ensure each NEW deploy announces exactly once
   *  per session, even if the polling loop re-detects on every tick. */
  lastAnnouncedVersion: string | null
}

export const INITIAL_DISMISSAL_STATE: DismissalState = {
  updateAvailable: null,
  dismissed: false,
  lastAnnouncedVersion: null,
}

export interface ProcessPollInput {
  /** Whatever `/version.json` returned. When null, the poll failed
   *  or returned an unparseable payload — caller should keep the
   *  previous state. */
  deployed: UpdateInfo | null
  /** The version stamped into the loaded bundle's
   *  `<meta name="app-version">`. Null when the meta tag is absent
   *  (older bundles); the helper short-circuits and returns the
   *  previous state because we have no baseline to compare against. */
  loadedVersion: string | null
}

export interface ProcessPollResult {
  next: DismissalState
  /** True ONLY when the helper transitioned from "not announced"
   *  → "announced" for this version. Caller fires the cross-app
   *  CustomEvent and onUpdateDetected callback when this is true. */
  shouldAnnounce: boolean
}

/**
 * Compute the next VersionWatcher state from the previous state +
 * the freshly-polled deployed version.
 *
 * Truth table:
 *
 *   loadedVersion  deployed       → result
 *   ─────────────  ─────────────  ──────────────────────────────────
 *   null            *              keep prev (no baseline)
 *   X               null           keep prev (poll failed)
 *   X               X              clear updateAvailable (matches!)
 *   X               Y (first time) updateAvailable=Y, dismissed=false,
 *                                  shouldAnnounce=true
 *   X               Y (re-detect)  updateAvailable=Y, dismissed UNCHANGED,
 *                                  shouldAnnounce=false
 *   X               Z (after Y)    updateAvailable=Z, dismissed=false,
 *                                  shouldAnnounce=true
 *
 * Crucially, when we go from "Y is detected and dismissed" → "Z is
 * now deployed", we RESET dismissed to false so the operator sees
 * the new banner. That's the bug fix.
 */
export function processVersionPoll(
  prev: DismissalState,
  input: ProcessPollInput,
): ProcessPollResult {
  if (input.loadedVersion == null) return { next: prev, shouldAnnounce: false }
  if (input.deployed == null) return { next: prev, shouldAnnounce: false }

  if (input.deployed.nextVersion === input.loadedVersion) {
    if (prev.updateAvailable == null && !prev.dismissed && prev.lastAnnouncedVersion == null) {
      return { next: prev, shouldAnnounce: false }
    }
    return {
      next: { ...prev, updateAvailable: null },
      shouldAnnounce: false,
    }
  }

  const isBrandNew = prev.lastAnnouncedVersion !== input.deployed.nextVersion
  return {
    next: {
      updateAvailable: { ...input.deployed },
      dismissed: isBrandNew ? false : prev.dismissed,
      lastAnnouncedVersion: isBrandNew
        ? input.deployed.nextVersion
        : prev.lastAnnouncedVersion,
    },
    shouldAnnounce: isBrandNew,
  }
}

/**
 * Apply the operator's "X dismissed" click. We deliberately keep
 * the rest of the state intact — the watcher doesn't re-show on
 * the same version, and the next NEW version will reset
 * `dismissed` via processVersionPoll above.
 */
export function applyDismissal(prev: DismissalState): DismissalState {
  return { ...prev, dismissed: true }
}
